import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  BarChart3, TrendingUp, Users, DollarSign, Briefcase,
  Download, Calendar, Filter, FileText, PieChart, Activity
} from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ReportsAnalytics = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [timeRange, setTimeRange] = useState('month');

  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error(i18n.language === 'ar' ? 'غير مصرح' : 'Unauthorized');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [timeRange]);

  const fetchData = async () => {
    try {
      const response = await api.get('/stats/dashboard');
      setStats(response.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل البيانات' : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const mainStats = [
    {
      title: i18n.language === 'ar' ? 'إجمالي القضايا' : 'Total Cases',
      value: stats?.total_cases || 0,
      change: '+12%',
      icon: Briefcase,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: i18n.language === 'ar' ? 'إجمالي المستخدمين' : 'Total Users',
      value: stats?.total_users || 0,
      change: '+8%',
      icon: Users,
      color: 'from-green-500 to-green-600'
    },
    {
      title: i18n.language === 'ar' ? 'الإيرادات' : 'Revenue',
      value: '285,000 ريال',
      change: '+23%',
      icon: DollarSign,
      color: 'from-yellow-500 to-yellow-600'
    },
    {
      title: i18n.language === 'ar' ? 'معدل النجاح' : 'Success Rate',
      value: '94.5%',
      change: '+3%',
      icon: TrendingUp,
      color: 'from-purple-500 to-purple-600'
    }
  ];

  const casesByDepartment = [
    { name: i18n.language === 'ar' ? 'القضايا العمالية' : 'Labor Cases', value: 45, color: 'bg-blue-500' },
    { name: i18n.language === 'ar' ? 'القضايا التجارية' : 'Commercial Cases', value: 38, color: 'bg-green-500' },
    { name: i18n.language === 'ar' ? 'القضايا العقارية' : 'Real Estate Cases', value: 32, color: 'bg-yellow-500' },
    { name: i18n.language === 'ar' ? 'قضايا الأسرة' : 'Family Cases', value: 28, color: 'bg-purple-500' },
    { name: i18n.language === 'ar' ? 'القضايا الجنائية' : 'Criminal Cases', value: 25, color: 'bg-red-500' }
  ];

  const monthlyData = [
    { month: i18n.language === 'ar' ? 'يناير' : 'Jan', cases: 45, revenue: 125000 },
    { month: i18n.language === 'ar' ? 'فبراير' : 'Feb', cases: 52, revenue: 138000 },
    { month: i18n.language === 'ar' ? 'مارس' : 'Mar', cases: 48, revenue: 142000 },
    { month: i18n.language === 'ar' ? 'أبريل' : 'Apr', cases: 61, revenue: 156000 },
    { month: i18n.language === 'ar' ? 'مايو' : 'May', cases: 55, revenue: 148000 },
    { month: i18n.language === 'ar' ? 'يونيو' : 'Jun', cases: 67, revenue: 172000 }
  ];

  const exportReport = (type) => {
    toast.success(i18n.language === 'ar' ? `جاري تصدير التقرير بصيغة ${type}` : `Exporting report as ${type}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">
                {i18n.language === 'ar' ? 'التقارير والإحصائيات' : 'Reports & Analytics'}
              </h1>
              <p className="text-text-secondary text-lg">
                {i18n.language === 'ar' ? 'تحليلات شاملة ورؤى ذكية للأداء' : 'Comprehensive analytics and performance insights'}
              </p>
            </div>
            <div className="flex gap-2">
              <Select value={timeRange} onValueChange={setTimeRange}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">{i18n.language === 'ar' ? 'أسبوع' : 'Week'}</SelectItem>
                  <SelectItem value="month">{i18n.language === 'ar' ? 'شهر' : 'Month'}</SelectItem>
                  <SelectItem value="year">{i18n.language === 'ar' ? 'سنة' : 'Year'}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => exportReport('PDF')}>
                <Download className="w-4 h-4 ml-2" />
                {i18n.language === 'ar' ? 'تصدير' : 'Export'}
              </Button>
            </div>
          </div>

          {/* Main Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mainStats.map((stat, index) => (
              <Card key={index} className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                <CardContent className="pt-6 relative">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-sm text-text-secondary mb-2">{stat.title}</p>
                      <p className="text-3xl font-bold">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-lg`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    <span className="text-sm font-semibold text-green-600">{stat.change}</span>
                    <span className="text-sm text-text-secondary">
                      {i18n.language === 'ar' ? 'من الشهر الماضي' : 'from last month'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="overview">{i18n.language === 'ar' ? 'نظرة عامة' : 'Overview'}</TabsTrigger>
              <TabsTrigger value="cases">{i18n.language === 'ar' ? 'القضايا' : 'Cases'}</TabsTrigger>
              <TabsTrigger value="finance">{i18n.language === 'ar' ? 'المالية' : 'Finance'}</TabsTrigger>
              <TabsTrigger value="performance">{i18n.language === 'ar' ? 'الأداء' : 'Performance'}</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Cases by Department */}
                <Card>
                  <CardHeader>
                    <CardTitle>{i18n.language === 'ar' ? 'القضايا حسب القسم' : 'Cases by Department'}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {casesByDepartment.map((dept, index) => (
                        <div key={index}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium">{dept.name}</span>
                            <span className="text-sm font-bold">{dept.value}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className={`${dept.color} h-2 rounded-full transition-all`}
                              style={{ width: `${(dept.value / 70) * 100}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Monthly Trend */}
                <Card>
                  <CardHeader>
                    <CardTitle>{i18n.language === 'ar' ? 'الاتجاه الشهري' : 'Monthly Trend'}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {monthlyData.map((data, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-semibold">{data.month}</p>
                            <p className="text-sm text-text-secondary">{data.cases} {i18n.language === 'ar' ? 'قضية' : 'cases'}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">{data.revenue.toLocaleString()} ريال</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="cases">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'تقرير القضايا المفصل' : 'Detailed Cases Report'}</CardTitle>
                  <CardDescription>
                    {i18n.language === 'ar' ? 'تحليل شامل لجميع القضايا حسب الحالة والقسم' : 'Comprehensive analysis of all cases by status and department'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm text-blue-600 mb-1">{i18n.language === 'ar' ? 'قضايا نشطة' : 'Active Cases'}</p>
                      <p className="text-2xl font-bold text-blue-700">42</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <p className="text-sm text-green-600 mb-1">{i18n.language === 'ar' ? 'قضايا مغلقة' : 'Closed Cases'}</p>
                      <p className="text-2xl font-bold text-green-700">156</p>
                    </div>
                    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <p className="text-sm text-yellow-600 mb-1">{i18n.language === 'ar' ? 'قضايا معلقة' : 'Pending Cases'}</p>
                      <p className="text-2xl font-bold text-yellow-700">18</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="finance">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'التقرير المالي' : 'Financial Report'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-6 bg-gradient-to-br from-green-500 to-green-600 rounded-xl text-white">
                      <p className="text-sm mb-2 opacity-90">{i18n.language === 'ar' ? 'إجمالي الإيرادات' : 'Total Revenue'}</p>
                      <p className="text-3xl font-bold">285,000 ريال</p>
                    </div>
                    <div className="p-6 bg-gradient-to-br from-red-500 to-red-600 rounded-xl text-white">
                      <p className="text-sm mb-2 opacity-90">{i18n.language === 'ar' ? 'المصروفات' : 'Expenses'}</p>
                      <p className="text-3xl font-bold">142,500 ريال</p>
                    </div>
                    <div className="p-6 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl text-white">
                      <p className="text-sm mb-2 opacity-90">{i18n.language === 'ar' ? 'صافي الربح' : 'Net Profit'}</p>
                      <p className="text-3xl font-bold">142,500 ريال</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="performance">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'تقرير الأداء' : 'Performance Report'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">{i18n.language === 'ar' ? 'متوسط وقت إغلاق القضية' : 'Avg. Case Resolution Time'}</span>
                        <span className="text-2xl font-bold text-primary">45 {i18n.language === 'ar' ? 'يوم' : 'days'}</span>
                      </div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">{i18n.language === 'ar' ? 'معدل رضا العملاء' : 'Client Satisfaction Rate'}</span>
                        <span className="text-2xl font-bold text-green-600">4.8/5.0</span>
                      </div>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold">{i18n.language === 'ar' ? 'نسبة نجاح القضايا' : 'Case Success Rate'}</span>
                        <span className="text-2xl font-bold text-blue-600">94.5%</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ReportsAnalytics;